/* global Stripe */
/* eslint-disable camelcase */
import axios from "axios";
import { BANK_ACCOUNT_TYPES, STRIPE_KEY } from "@constants";

const constants = {
	BANK_ACCOUNT_TYPES: {
		INDIVIDUAL: { value: 1, label: "Individual" },
		COMPANY: { value: 2, label: "Company" },
	},
};

/**
 * @param image
 * @param purpose
 * @returns {Promise}
 */
const createImageToken = async (image, purpose = "identity_document") => {
	const imageData = new FormData();
	imageData.append("file", image);
	imageData.append("purpose", purpose);

	try {
		const token = await axios.post("https://files.stripe.com/v1/files", imageData, {
			headers: {
				Authorization: `Bearer ${STRIPE_KEY}`,
				"Content-Type": "multipart/form-data",
			},
		});
		return token.data;
	} catch (error) {
		return { ...error.response.data };
	}
};

/**
 * @param formData {Object} User data
 * @param imageToken {String} Image stripe token
 * @returns {Promise}
 */
const createAccountToken = async (formData, imageToken = null) => {
	// account_holder_type must be an integer value: individual (1) or company (2)
	// id_number, first_name, last_name, dateOfBirth, email and imageToken are needed only for individual accounts,
	// tax_id and name are needed only for company accounts
	// state must the be name of the state not the id
	// dob must be formatted 'MM/DD/YYYY'
	const {
		account_holder_type,
		city,
		state,
		postal_code,
		dateOfBirth,
		line1,
		id_number,
		phoneNumber,
		first_name,
		last_name,
		email,
		tax_id,
		name,
	} = formData;

	const address = {
		country: "US",
		line1,
		postal_code,
		state,
		city,
	};

	const parsePhoneNumber = number => {
		const cleanPhone = number.replace(/\D/g, "");
		const phoneMatch = cleanPhone.match(/^(\d{3})(\d{3})(\d{4})$/);
		return `${phoneMatch[1]}-${phoneMatch[2]}-${phoneMatch[3]}`;
	};

	const phone = phoneNumber ? parsePhoneNumber(phoneNumber) : undefined;
	const dobArray = dateOfBirth ? dateOfBirth.split("/") : [];
	const dob = { day: dobArray[1], month: dobArray[0], year: dobArray[2] };
	const accountType = Object.values(BANK_ACCOUNT_TYPES).find(
		item => item.value === parseInt(account_holder_type, 10),
	);

	const accountData = {
		business_type: accountType.label.toLowerCase(),
		tos_shown_and_accepted: true,
	};

	if (accountType.value === constants.BANK_ACCOUNT_TYPES.INDIVIDUAL.value) {
		accountData.individual = {
			address,
			first_name,
			last_name,
			id_number,
			dob,
			verification: {
				document: {
					front: imageToken,
				},
			},
			phone,
			email,
		};
	} else if (accountType.value === constants.BANK_ACCOUNT_TYPES.COMPANY.value) {
		accountData.company = {
			address,
			tax_id,
			phone,
			name,
		};
	}

	const stripe = Stripe(STRIPE_KEY);
	const { error, token } = await stripe.createToken("account", accountData);

	if (error) {
		return { error };
	}

	return { token };
};

/**
 * @param {Object} formData Bank account data
 * @returns {Promise}
 */
const createBankAccountToken = async ({
	account_holder_name,
	routing_number,
	account_number,
	account_holder_type,
}) => {
	const bankAccountType = Object.values(BANK_ACCOUNT_TYPES)
		.find(item => item.value === parseInt(account_holder_type, 10))
		.label.toLowerCase();

	const bankAccountData = {
		account_holder_name,
		routing_number,
		account_number,
		country: "US",
		currency: "USD",
		account_holder_type: bankAccountType,
	};

	const stripe = Stripe(STRIPE_KEY);
	const { error, token } = await stripe.createToken("bank_account", bankAccountData);

	if (error) {
		return { error };
	}

	return { token };
};

export default {
	createImageToken,
	createAccountToken,
	createBankAccountToken,
};
